import type { CSSProperties } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { SiteLanguage } from '@/lib/site-language-config';

// Hero visual: a phone-style feed of the kind of events our tools produce for a client.
// Pure CSS animation (see .hero-activity-item in marketing-theme.css): no images, no JS timers.
const copy = {
  en: {
    live: 'Your business, live',
    today: 'Today',
    count: '12',
    countLabel: 'appointments',
    amount: '87,500',
    currency: 'FCFA',
    items: [
      { icon: 'CalendarDaysIcon', title: 'New booking', detail: 'Braids · Saturday 2:30 pm', time: 'now' },
      { icon: 'DevicePhoneMobileIcon', title: 'MoMo payment received', detail: '15,000 FCFA · Ref. 8F21K', time: '2 min' },
      { icon: 'QrCodeIcon', title: 'QR badge checked in', detail: 'Member entry · Main gym', time: '5 min' },
      { icon: 'GiftIcon', title: 'Loyalty card 5/6', detail: 'Next visit is free', time: '9 min' },
    ],
  },
  fr: {
    live: 'Votre activité, en direct',
    today: 'Aujourd’hui',
    count: '12',
    countLabel: 'rendez-vous',
    amount: '87 500',
    currency: 'FCFA',
    items: [
      { icon: 'CalendarDaysIcon', title: 'Nouvelle réservation', detail: 'Tresses · Samedi 14:30', time: 'à l’instant' },
      { icon: 'DevicePhoneMobileIcon', title: 'Paiement MoMo reçu', detail: '15 000 FCFA · Réf. 8F21K', time: '2 min' },
      { icon: 'QrCodeIcon', title: 'Badge QR validé', detail: 'Entrée membre · Salle principale', time: '5 min' },
      { icon: 'GiftIcon', title: 'Carte de fidélité 5/6', detail: 'Prochaine visite offerte', time: '9 min' },
    ],
  },
} as const;

interface HeroActivityProps {
  language: SiteLanguage;
}

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

// Each digit is a tile holding a 0-9 strip that rolls to the target digit; separators stay static.
function SplitFlap({ value, delay }: { value: string; delay: number }) {
  return (
    <span className="inline-flex items-center font-sans text-small font-semibold text-primary [font-variant-numeric:tabular-nums]">
      {value.split('').map((char, index) =>
        /\d/.test(char) ? (
          <span key={index} className="flap-tile">
            <span
              className="flap-strip"
              style={{ '--d': Number(char), animationDelay: `${delay + index * 0.08}s` } as CSSProperties}
            >
              {DIGITS.map((digit) => (
                <span key={digit} className="flap-digit">
                  {digit}
                </span>
              ))}
            </span>
          </span>
        ) : (
          <span key={index} className="px-[0.1em]">
            {char === ' ' ? ' ' : char}
          </span>
        )
      )}
    </span>
  );
}

export default function HeroActivity({ language }: HeroActivityProps) {
  const t = copy[language];

  return (
    <div
      className="mx-auto w-full max-w-sm rounded-[12px] border border-white/10 bg-card/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_24px_60px_-20px_rgba(34,211,211,0.25)] backdrop-blur-sm sm:p-5 lg:ml-auto lg:mr-0"
      aria-hidden="true"
    >
      <div className="flex items-center justify-between px-1">
        <p className="inline-flex items-center gap-2 font-sans text-caption font-medium text-foreground">
          <span className="hero-activity-dot relative inline-flex h-2 w-2 rounded-full bg-primary" />
          {t.live}
        </p>
        <p className="font-mono text-caption text-muted-foreground">{t.today}</p>
      </div>

      {/* overflow-hidden: rows slide in from the card edges without widening the page on phones */}
      <ul className="-mx-1 mt-3 space-y-2.5 overflow-hidden px-1 py-1">
        {t.items.map((item, index) => (
          <li
            key={item.title}
            className={`hero-activity-item ${index % 2 === 1 ? 'hero-activity-item--right' : ''} flex items-center gap-3 rounded-[8px] border border-white/5 bg-background/70 p-3`}
            style={{ animationDelay: `${0.3 + index * 0.6}s` }}
          >
            <span className="hero-activity-icon flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[6px] bg-primary/10 text-primary">
              <Icon name={item.icon} size={20} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-sans text-small font-medium text-foreground">{item.title}</span>
              <span className="block truncate font-sans text-caption text-muted-foreground">{item.detail}</span>
            </span>
            <span className="flex-shrink-0 font-sans text-caption text-muted-foreground">{item.time}</span>
          </li>
        ))}
      </ul>

      {/* Split-flap totals: digits clack into place each loop (see .flap-* in marketing-theme.css) */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-[8px] bg-primary/10 px-3 py-3 sm:px-4">
        <span className="inline-flex items-center gap-2 font-sans text-caption text-muted-foreground">
          <SplitFlap value={t.count} delay={0.5} />
          {t.countLabel}
        </span>
        <span className="inline-flex items-center gap-2 font-sans text-caption text-muted-foreground">
          <SplitFlap value={t.amount} delay={0.8} />
          {t.currency}
        </span>
      </div>
    </div>
  );
}
